import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import * as sessionUserSelector from 'selectors/sessionUser'

import { userTimeZoneFormat } from 'utils/time'

import { complianceLabel, complianceColorsByLabel } from 'utils/satCompliance'

import Sidebar from 'components/Shared/Sidebar/RightSidebar'
import SidebarSection from 'components/Shared/Sidebar/SidebarSection/Index'
import SidebarSectionBool from 'components/Shared/Sidebar/SidebarSectionBool/Index'
import SidebarSectionTable from 'components/Shared/Sidebar/SidebarSectionTable/Index'
import SidebarBadges from 'components/Shared/Sidebar/SidebarBadges/Index'
import SidebarText from 'components/Shared/Sidebar/SidebarText/Index'
import SatComplianceBadge from 'components/App/SatClientsPage/shared/SatComplianceBadge'

const SatClientSidebar = ({ satClient, onClose }) => {
  const { t } = useTranslation()
  const userTimeZone = useSelector(sessionUserSelector.timezone)

  if (!satClient) {
    return null
  }

  const capabilityBadges = satClient.capabilities.map(capability => ({
    value: capability,
    title: capability,
  }))

  const secretRows = satClient.satSecrets.map(satSecret => ({
    lastFour: satSecret.lastFour.slice(-4),
    active: satSecret.active,
    expiresAt: userTimeZoneFormat(userTimeZone, satSecret.expiresAt, 'date'),
    compliance: complianceLabel(t, satSecret.complianceStatus),
  }))

  const secretColumns = {
    'lastFour': { title: t('satClientsPage.rightSideBar.secretsColumns.lastFour') },
    'active': { title: t('satClientsPage.rightSideBar.secretsColumns.active'), type: 'boolean' },
    'expiresAt': { title: t('satClientsPage.rightSideBar.secretsColumns.expiresAt'), noWrap: true },
    'compliance': {
      title: t('satClientsPage.rightSideBar.secretsColumns.compliance'),
      format: 'badge',
      colorsMap: complianceColorsByLabel(t),
    },
  }

  return (
    <Sidebar
      key={satClient.id}
      onClose={onClose}
      title={t('satClientsPage.rightSideBar.header')}
      dataTest='sat-client-details'
    >
      <SidebarSection
        title={t('satClientsPage.rightSideBar.sectionClientId')}
        copyText={satClient.clientId}
      >
        <SidebarText text={satClient.clientId} />
      </SidebarSection>

      <SidebarSection title={t('satClientsPage.rightSideBar.sectionCompliance')}>
        <SatComplianceBadge status={satClient.complianceStatus} />
      </SidebarSection>

      <SidebarSection
        title={t('satClientsPage.rightSideBar.sectionDescription')}
        copyText={satClient.description}
      >
        <SidebarText text={satClient.description} />
      </SidebarSection>

      <SidebarSection title={t('satClientsPage.rightSideBar.sectionCapabilities')}>
        <SidebarBadges badges={capabilityBadges} />
      </SidebarSection>

      <SidebarSectionBool
        title={t('satClientsPage.rightSideBar.sectionEnabled')}
        value={satClient.enabled}
      />

      <SidebarSectionBool
        title={t('satClientsPage.rightSideBar.sectionApproved')}
        value={satClient.approved}
      />

      <SidebarSectionTable
        title={t('satClientsPage.rightSideBar.sectionSecrets')}
        values={secretRows}
        columnsOrder={['lastFour', 'active', 'expiresAt', 'compliance']}
        columns={secretColumns}
      />

      <SidebarSection
        title={t('satClientsPage.rightSideBar.sectionCreatedAt')}
        copyText={userTimeZoneFormat(userTimeZone, satClient.createdAt)}
      >
        <SidebarText text={userTimeZoneFormat(userTimeZone, satClient.createdAt)} />
      </SidebarSection>

      <SidebarSection
        title={t('satClientsPage.rightSideBar.sectionUpdatedAt')}
        copyText={userTimeZoneFormat(userTimeZone, satClient.updatedAt)}
      >
        <SidebarText text={userTimeZoneFormat(userTimeZone, satClient.updatedAt)} />
      </SidebarSection>
    </Sidebar>
  )
}

SatClientSidebar.propTypes = {
  satClient: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

export default SatClientSidebar
